import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import { DrawerClose } from "common/components/ui/Drawer";
import { PopoverClose } from "common/components/ui/Popover";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useIntegrationMutations from "../../datahooks/useIntegrationMutations";
import {
  EmailBisonCustomUrlSchema,
  type EmailBisonCustomUrlSchemaType,
} from "../../schema";
import EmailBisonCustomUrlField from "../EmailBisonCustomUrlField";
import IntegrationAccountField from "./IntegrationAccountField";
import IntegrationSettingsError from "./IntegrationSettingsError";
import RemoveIntegrationButton from "./RemoveIntegrationButton";
import SettingsContainer from "./SettingsContainer";

type EmailBisonSettingsProps =
  | { userName: string; customUrl: string | null }
  | { errorMessage: string };

export default function EmailBisonSettings(props: EmailBisonSettingsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const hasError = "errorMessage" in props;

  const { updateEmailBisonIntegration, isUpdatingEmailBisonIntegration } =
    useIntegrationMutations();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: valibotResolver(EmailBisonCustomUrlSchema),
    defaultValues: { custom_url: hasError ? "" : (props.customUrl ?? "") },
  });

  function onSubmit({ custom_url }: EmailBisonCustomUrlSchemaType) {
    updateEmailBisonIntegration(custom_url || null).then(() =>
      reset({ custom_url }),
    );
  }

  const Close = isDesktop ? PopoverClose : DrawerClose;

  const footerContent = (
    <>
      <Close asChild>
        <Button
          variant="secondary-black"
          size="md"
          disabled={isUpdatingEmailBisonIntegration}
        >
          Cancel
        </Button>
      </Close>

      <Button
        size="md"
        isLoading={isUpdatingEmailBisonIntegration}
        onClick={handleSubmit(onSubmit)}
      >
        Save
      </Button>
    </>
  );

  return (
    <SettingsContainer
      integrationKey="emailbison"
      footerContent={hasError ? null : footerContent}
    >
      {hasError ? (
        <IntegrationSettingsError errorMessage={props.errorMessage} />
      ) : (
        <>
          <IntegrationAccountField
            label="EmailBison user"
            value={props.userName}
          />

          <EmailBisonCustomUrlField
            error={errors.custom_url?.message}
            registration={register("custom_url")}
          />
        </>
      )}
      <RemoveIntegrationButton integrationKey="emailbison" />
    </SettingsContainer>
  );
}
